import * as THREE from "three";
import { SignalType } from "./connections";

/**
 * Rounds a double to the shortest decimal that round-trips to the same float32.
 * The engine runs all physics/positions through float32 GPU uniforms, so the
 * extra double digits in saved scene JSON are discarded on load anyway. Storing
 * the shortest float32-equivalent keeps the serialized value bit-identical to
 * what the GPU computes while shrinking the JSON.
 */
export function f32(x: number): number {
  const f = Math.fround(x);
  for (let p = 1; p <= 9; p += 1) {
    const s = Number(f.toPrecision(p));
    if (Math.fround(s) === f) {
      return s;
    }
  }
  return f;
}

const PARTICLE_TEXTURE_WIDTH_DEFAULT = 128;
const PARTICLE_TEXTURE_WIDTH_MIN = 16;
const PARTICLE_TEXTURE_WIDTH_MAX = 256;

/**
 * The GPGPU simulation grid is a square FloatTexture of edge N, so particle
 * count is N². N is fixed once at module init from ?particles=N, clamped to
 * [16, 256] (= 256..65536 particles; the upper bound is a VRAM / mobile-GPU
 * budget ceiling, not a hard cap — raise it only after measuring on the target
 * device). A missing or non-numeric param keeps the 128 (= 16384-particle)
 * default. The URL must be read here, at constants module init, because
 * particles.ts bakes N into its shader source via template literals when that
 * module is first imported; resolving N before then is what lets a reload pick
 * a new count without making the shaders dynamically sized.
 */
function resolveParticleTextureWidth(): number {
  if (typeof location === "undefined") {
    return PARTICLE_TEXTURE_WIDTH_DEFAULT;
  }
  const raw = new URLSearchParams(location.search).get("particles");
  if (raw === null) {
    return PARTICLE_TEXTURE_WIDTH_DEFAULT;
  }
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) {
    return PARTICLE_TEXTURE_WIDTH_DEFAULT;
  }
  return Math.max(PARTICLE_TEXTURE_WIDTH_MIN, Math.min(PARTICLE_TEXTURE_WIDTH_MAX, n));
}

export const PARTICLE_TEXTURE_WIDTH = resolveParticleTextureWidth();
export const PARTICLE_COUNT = PARTICLE_TEXTURE_WIDTH * PARTICLE_TEXTURE_WIDTH;

export const VIEW_HEIGHT = 20;

export const CAMERA_FOV_2D = 14;
export const CAMERA_FOV_3D = 50;
export const CAMERA_NEAR = 0.1;
export const CAMERA_FAR = 500;
export const CAMERA_3D_PHI = 1.15;
export const CAMERA_3D_THETA = 0.5;
export const VIEW_TWEEN_S = 0.9;

export const CAMERA_FIT_MARGIN = 1.1;
export const CAMERA_FIT_ASPECT_THRESHOLD = 0.01;
export const CAMERA_FIT_DETECT_NDC = 1.0;

export const EMITTER_POSITION = new THREE.Vector3(0, -6, 0);
export const EMITTER_SEED = 1.37;
export const EMIT_SPEED_MIN = 10;
export const EMIT_SPEED_MAX = 10;
export const EMIT_CONE_HALF_ANGLE = 0.175;

export const EMIT_RATE_DEFAULT = 240;
export const EMIT_RATE_MIN = 1;
export const EMIT_RATE_MAX = 8000;

export const MAX_EMITTERS = 8;
export const EMITTER_GIZMO_RADIUS = 0.35;
export const EMITTER_GIZMO_HEIGHT = 0.9;
export const EMITTER_COLOR = new THREE.Color(0xffb347);

export const GRAVITY = new THREE.Vector3(0, -9.8, 0);
export const GRAVITY_Y_MIN = -30;
export const GRAVITY_Y_MAX = 0;

export const FALL_KILL_DIST_MIN = 5;
export const FALL_KILL_DIST_MAX = 100;
export const FALL_KILL_DIST_DEFAULT = 20;
export const FALL_KILL_LIFE_DIV_MIN = 1;
export const FALL_KILL_LIFE_DIV_MAX = 16;
export const FALL_KILL_LIFE_DIV_DEFAULT = 4;

export const LIFESPAN_MIN = 10.0;
export const LIFESPAN_MAX = 10.0;
export const LIFESPAN_SLIDER_MAX = 60;
/** Stream emitter rate ceiling = this / lifeMax (particles/s × s); raising lifetime lowers the allowed rate. */
export const STREAM_RATE_LIFE_BUDGET = 24000;

export const PARTICLE_WORLD_SIZE = 0.3;
export const PARTICLE_COLOR = new THREE.Color(0x8fd3ff);

export const OBJECT_METALNESS = 0.0;
export const OBJECT_ROUGHNESS = 0.35;

export const PITCH_SCALE_LEN = 5;
export const PITCH_SATURATION = 0.8;
export const PITCH_LIGHTNESS_MIN = 0.05;
export const PITCH_LIGHTNESS_MAX = 0.65;
export const PITCH_OCTAVE_MIN = -3;
export const PITCH_OCTAVE_MAX = 3;
export const PREVIEW_COLOR = 0x9aa3ad;
export const KEY_COLOR_BLACK = 0x202020;
export const KEY_COLOR_WHITE = 0xf5f5f5;

export const METAL_METALNESS = 0.85;
export const METAL_ROUGHNESS = 0.25;

export const CAP_Z_OFFSET = 1e-3;

export const KEY_LIGHT_DIR_X = -0.07;
export const KEY_LIGHT_DIR_Y = 0.22;
export const KEY_LIGHT_DIR_Z = 0.95;
export const KEY_LIGHT_DIR = new THREE.Vector3(KEY_LIGHT_DIR_X, KEY_LIGHT_DIR_Y, KEY_LIGHT_DIR_Z).normalize();
export const KEY_LIGHT_INTENSITY = 1.8;
export const KEY_LIGHT_DISTANCE = 60;
export const SHADOW_MAP_SIZE = 2048;
export const ENV_INTENSITY = 0.25;
export const TONE_MAPPING_EXPOSURE = 1.0;

export const BLOOM_STRENGTH = 0.55;
export const BLOOM_RADIUS = 0.4;
export const BLOOM_THRESHOLD = 1.1;

export const ADAPTIVE_TARGET_FPS_CAP = 60;
export const ADAPTIVE_TARGET_FPS_FLOOR = 30;
export const ADAPTIVE_REFRESH_RATES = [30, 60, 90, 120, 144];
export const ADAPTIVE_WARMUP_MS = 1000;
export const ADAPTIVE_FPS_RATIO = 0.9;
export const ADAPTIVE_EVAL_MS = 1500;
export const ADAPTIVE_SETTLE_MS = 1000;

export const SELECTION_FRAME_COLOR = new THREE.Color(0xffffff);
export const WIRING_TARGET_COLOR = new THREE.Color(0xff9500);
export const FILL_RING_COLOR = `#${WIRING_TARGET_COLOR.getHexString()}`;
/**
 * Stroke color per port signal type, used by the connection overlay so a wire
 * matches the color of the port dot it leaves. The same values are mirrored in
 * style.css on `.port-dot.port-{pulse,value,transform}`; keep the two in sync.
 */
export const PORT_TYPE_COLOR: Record<SignalType, string> = {
  pulse: "#ffb347",
  value: "#6cf0a0",
  transform: "#c9a0ff",
  envelope: "#f0a0c0",
  pitchQuantize: "#7cc0f0",
};

export const CONTACT_EMISSIVE_PEAK = 0.0;
export const CONTACT_EMISSIVE_DECAY_TAU = 0.18;
export const CONTACT_IMPULSE_PEAK = 0.03;
export const CONTACT_IMPULSE_DECAY_TAU = 0.11;

export const PARTICLE_GLOW_PEAK = 0.0;
export const PARTICLE_GLOW_DECAY_TAU = 0.13;
export const PARTICLE_GLOW_COLOR = new THREE.Color(0xffffff);

export const PARTICLE_AMBIENT = 0.4;
export const PARTICLE_SPECULAR = 0.35;
export const PARTICLE_SHININESS = 24.0;
export const PARTICLE_RIM = 0.18;

export const MAX_DELTA = 1 / 30;
export const FIXED_DT = 1 / 120;

export const CLOCK_BPM_MIN = 15;
export const CLOCK_BPM_MAX = 400;
export const CLOCK_PERIOD_DEFAULT = 0.5;
export const CLOCK_PERIOD_MIN = 60 / CLOCK_BPM_MAX;
export const CLOCK_PERIOD_MAX = 60 / CLOCK_BPM_MIN;
export const TAP_TEMPO_MIN_TAPS = 3;
export const TAP_TEMPO_TIMEOUT_MS = 2000;
export const SEQ_NUM_DEFAULT = 8;
export const SEQ_MAX_ROWS = 100;

export const MAX_OBJECTS = 2048;

export const CONTACT_RING_SIZE_DEFAULT = 3;
export const CONTACT_RING_SIZE_MIN = 2;
export const CONTACT_RING_SIZE_MAX = 8;

/**
 * Number of contact-histogram render targets in the read-back ring. Each frame
 * one slot's accumulation window is issued as a non-blocking async read and the
 * write rotates to the next slot, so up to N-1 reads can be in flight at once —
 * this restores per-frame onset delivery (a single in-flight gate collapsed
 * several frames of contacts into one batch, dropping rapid-fire onsets). N is
 * the cadence-vs-tiler-flush knob: larger N recovers cadence but keeps more PBO
 * reads concurrent, which can re-trigger the mobile readPixels flush stall the
 * async path exists to avoid. Overridable via ?contactring=N to retune the
 * minimum N on real hardware without a rebuild.
 */
function resolveContactRingSize(): number {
  if (typeof location === "undefined") {
    return CONTACT_RING_SIZE_DEFAULT;
  }
  const raw = new URLSearchParams(location.search).get("contactring");
  if (raw === null) {
    return CONTACT_RING_SIZE_DEFAULT;
  }
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) {
    return CONTACT_RING_SIZE_DEFAULT;
  }
  return Math.max(CONTACT_RING_SIZE_MIN, Math.min(CONTACT_RING_SIZE_MAX, n));
}

export const CONTACT_RING_SIZE = resolveContactRingSize();

/**
 * The RGBA8-pack contact readback (a float-to-bytes encode pass + UNSIGNED_BYTE
 * readPixels) is the default on every platform: it is the only path that works
 * on iOS Safari (which renders to float RTs but reads zero back from them) and
 * is exact everywhere. ?packreadback=0 opts back to the direct async float
 * readback for low-spec Android A/B, where the pack path's synchronous read can
 * re-introduce the GPU-flush stall the async path avoids.
 */
export const CONTACT_READBACK_PACK: boolean =
  typeof location === "undefined" || new URLSearchParams(location.search).get("packreadback") !== "0";

/**
 * ?halfcontact=1 forces the contact RT to RGBA16F (half-float) even where
 * EXT_float_blend is present. The contact RT is half-float wherever that
 * extension is absent (iOS Safari, so its additive accumulation blend works);
 * this override exercises that same path on desktop, where float32 is otherwise
 * used.
 */
export const CONTACT_FORCE_HALF: boolean =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("halfcontact") === "1";

/**
 * Spatial-grid collision tuning. Passive small colliders are binned into a 2D
 * (XY) uniform grid so each particle only tests its neighbouring cells; force
 * sources (mass / field) and large colliders bypass the grid via the always-test
 * list (see objects.ts pack()). GRID_CELL_SIZE is the base cell edge — kept well
 * above the particle contact radius so a single substep step stays within the
 * 3×3 neighbourhood the shader scans (the TOI safety bound). A collider whose
 * bounding diameter spans more than GRID_LARGE_SPAN_CELLS base cells is sent to
 * the always-test list instead of binning (bounds per-cell occupancy). When the
 * grid object span exceeds GRID_MAX_DIM cells the effective cell size grows so
 * the grid stays within GRID_MAX_DIM² head texels (a coarser, still-correct
 * grid). GRID_IDX_TEX_W is the width of the CSR flat-index texture.
 */
export const GRID_CELL_SIZE = 2.0;
export const GRID_MAX_DIM = 128;
export const GRID_LARGE_SPAN_CELLS = 4;
export const GRID_IDX_TEX_W = 1024;

export const COLLISION_RADIUS = 0.15;
export const SDF_EPS = 0.02;
export const COLLISION_SUBSTEPS = 4;

export const DEFAULT_RESTITUTION = 1.0;
export const DEFAULT_FRICTION = 1.0;

export const EMIT_BOUNCE_DEFAULT = 1.0;
export const EMIT_FRICTION_DEFAULT = 0.0;
export const EMIT_DRAG_DEFAULT = 0.0;
export const EMIT_DRAG_MAX = 5.0;

export const DEFAULT_MASS = 3.0;
export const MASS_MAX = 6.0;
export const GRAVITY_CONSTANT = 80;
export const GRAVITY_SOFTENING = 0.6;

export const VOICE_CAP = 24;
export const CHOKE_FADE_S = 0.03;
export const SAMPLE_ONSET_FADE_S = 0.003;
export const SUSTAIN_RELEASE_S = 0.15;
export const SUSTAIN_DAMP_HOLD_S = 0.35;
export const SUSTAIN_DAMP_TAU = 0.08;
export const SUSTAIN_HOLD_THRESHOLD = 0.99;
export const AUDIO_BASE_MIDI = 60;
export const AUDIO_COOLDOWN_MS = 15;
export const AUDIO_LOOKAHEAD_S = 0.03;

/**
 * Latency budget the sim-clock-locked onset scheduler adds when reserving a
 * single-hit contact onset at its sim-anchored ctx time. It must exceed the
 * spread of (now - windowSim) around the tracked clock anchor — the contact
 * window's substep quantization (~one frame) plus readback jitter — so the
 * reserved time stays in the future and never falls back to the frame-arrival
 * `now` (which would reintroduce the frame-phase jitter this fix removes). Sized
 * from the T1 onset-diag measurement (window-quant maxAbsDev ~25ms @30fps);
 * chosen for rhythm precision over latency (user decision 2026-06-24). Applies
 * only to particle-contact onsets, not to playTap (interactive latency unchanged).
 */
export const AUDIO_ONSET_LATENCY_S = 0.05;

/**
 * ?onsetlock=0 disables the sim-clock-locked onset scheduling, reverting
 * single-hit contact onsets to the frame-arrival `now` schedule. The escape
 * hatch for A/B measurement and for opting out of the AUDIO_ONSET_LATENCY_S
 * latency if it is ever objectionable. Default on.
 */
export const AUDIO_ONSET_LOCK: boolean =
  typeof location === "undefined" || new URLSearchParams(location.search).get("onsetlock") !== "0";

/**
 * Per-delivery low-pass coefficient for the sim↔ctx clock anchor the onset
 * scheduler maps through. The anchor offset (ctxForSim(s) = s + offset) tracks
 * the slow baseline of (now - windowSim) so it follows clock drift — chiefly the
 * accumulator-clamp deficit a stalled frame leaves between sim `elapsed` and
 * ctx.currentTime — while rejecting most per-delivery jitter. Small enough to
 * behave like a fixed anchor on a steady high-fps machine (drift ≈ 0), large
 * enough that the reserved time does not progressively fall into the past under
 * drift. The anchor is never snapped: a discontinuous re-anchor would shift a
 * scheduled onset audibly.
 */
export const AUDIO_ONSET_ANCHOR_FOLLOW = 0.2;
export const AUDIO_PREWARM_GAIN = 0.0003;
export const AUDIO_PREWARM_VOICES = 32;
export const AUDIO_GAIN_PER_HIT = 0.22;
export const AUDIO_COUNT_EXP = 0.0001;
export const AUDIO_GAIN_MAX = 0.9;
export const IMPACT_MAX = 30;
export const IMPACT_REF = 12;
export const IMPACT_MIN = 2;
export const CONTACT_RELEASE_FACTOR = 1.1;
export const AUDIO_ATTACK_S = 0.005;
export const AUDIO_MIN_ATTACK_S = 0.003;
export const AUDIO_DECAY_S = 0.28;

/**
 * Full-scale seconds a normalized [0,1] envelope knob maps to for attack /
 * decay / release. Sustain is a level (no time), so it has no max. A knob at 1
 * gives the longest segment; the defaults sit low so a freshly wired envelope
 * stays close to the recipe AD voicing.
 */
export const ENVELOPE_ATTACK_MAX_S = 2.0;
export const ENVELOPE_DECAY_MAX_S = 3.0;
export const ENVELOPE_RELEASE_MAX_S = 4.0;
export const AUDIO_WOOD_DECAY_S = 1.0;
export const AUDIO_BELL_TRANSPOSE = 2;
export const AUDIO_BELL_MOD_RATIO = 5.5;
export const AUDIO_BELL_MOD_INDEX = 3.6;
export const AUDIO_BELL_LEVEL = 0.4;
export const AUDIO_MASTER_GAIN = 0.5;
export const AUDIO_DCBLOCK_HZ = 24;
export const AUDIO_DENSITY_RATE_LOW = 10;
export const AUDIO_DENSITY_RATE_HIGH = 70;
export const AUDIO_DENSITY_TAU = 0.3;
export const AUDIO_DENSITY_ATTACK_ADD_S = 0.04;
export const AUDIO_DENSITY_DECAY_MUL = 0.6;
export const AUDIO_DENSITY_TRANSIENT = 1.0;
export const AUDIO_DENSITY_DETUNE_CENTS = 20;
export const AUDIO_DENSITY_GAIN_FLOOR = 0.6;
export const AUDIO_DENSITY_DUCK_FLOOR = 0.7;
export const AUDIO_DENSITY_DUCK_TAU = 0.08;
export const AUDIO_BLUR_FADE_TAU = 0.25;
export const BLUR_PAUSE_DELAY_MS = 800;
export const REVERB_IR_SECONDS = 2.0;
export const REVERB_IR_DECAY = 0.6;
export const REVERB_SEND_MAX = 0.5;
export const REVERB_AMOUNT_DEFAULT = 0.5;
export const REVERB_SEND_TAU = 0.1;
export const LIMITER_THRESHOLD_DB = -6;
export const LIMITER_KNEE_DB = 0;
export const LIMITER_RATIO = 20;
export const LIMITER_ATTACK_S = 0.003;
export const LIMITER_RELEASE_S = 0.1;

export const DEFAULT_BOX_HALF = new THREE.Vector3(0.5, 1.0, 1.0);
export const DEFAULT_SPHERE_RADIUS = 1.3;
export const DEFAULT_FUNNEL = {
  rBottom: 0.7,
  rTop: 2.6,
  halfHeight: 1.9,
  thickness: 0.125,
};

export const GRID_SIZE = 120;
export const GRID_DIVISIONS = GRID_SIZE / 5;
export const GRID_COLOR = new THREE.Color(0x9aa3d4);
export const GRID_OPACITY = 0.4;

export const PLACE_BOUND_XY = 30; // world metre half-extent for placement x/y
export const PLACE_BOUND_Z = 30; // world metre half-extent for placement z (3D only)
export const PLACE_BOUND_MASK_COLOR = 0x3c3c42; // gray tint laid over the wallpaper outside the placement bounds
export const PLACE_BOUND_MASK_OPACITY = 0.5; // strength of the out-of-bounds gray tint
export const CAMERA_TARGET_BOUND = 30; // world metre half-extent the orbit target may pan to
export const CAMERA_MAX_DISTANCE = 120; // perspective dolly-out cap in world metre
export const CAMERA_MIN_DISTANCE = 3; // perspective dolly-in floor in world metre
export const ORTHO_MIN_ZOOM = 0.35; // ortho zoom-out floor (INITIAL_ORTHO_ZOOM=1 baseline)

export const FORCE_COLOR = new THREE.Color(0x6cf0d0);
export const FIELD_RADIUS_MIN = 0.5;
export const FIELD_RADIUS_MAX = 10.0;
export const FIELD_STRENGTH_MAX = 20.0;
export const FIELD_OMEGA_MAX = 12.0;
export const FIELD_K_MAX = 12.0;
export const DEFAULT_FIELD_RADIUS = 4.0;
export const DEFAULT_FIELD_FADE = 0.25;
export const DEFAULT_FIELD_MODE = 1;
export const DEFAULT_FIELD_STRENGTH = 6.0;
export const DEFAULT_FIELD_OMEGA = 8.0;
export const DEFAULT_FIELD_K = 4.0;

export const DECAY_DEFAULT_S = 1.0;
export const DECAY_MAX_S = 10.0;

export const DELAY_DEFAULT_S = 0.5;
export const DELAY_MAX_S = 10.0;
export const DELAY_QUEUE_CAP = 100;

export const ROUTER_HOLD_DEFAULT_S = 0;
export const ROUTER_HOLD_MAX_S = 10.0;

export const KNOB_DEFAULT_VALUE = 0.5;
export const KNOB_MIN_DEFAULT = 0;
export const KNOB_MAX_DEFAULT = 1;
export const KNOB_INT_DEFAULT_MAX = 5;
export const LAMP_FLASH_DECAY_S = 0.5;
export const LAMP_PEAK_MIN = 1.3;
export const LAMP_PEAK_MAX = 2.6;
export const LAMP_BLOOM_LUMA_MARGIN = 0.15;
export const LAMP_DEFAULT_HUE = 0.13;
export const LAMP_DEFAULT_SAT = 1;

export const PITCH_INDEX_MIN = -36;
export const PITCH_INDEX_MAX = 24;
export const DEFAULT_PITCH_INDEX = 0;

export const TRANSPOSE_RANGE = 24;

export const RAIL_MAX_POINTS = 8;
export const RAIL_SEGMENTS = 24;
export const RAIL_SAMPLES = RAIL_SEGMENTS + 1;
export const RAIL_TUBE_FACTOR = 0.25;
export const RAIL_TUBE_MIN = 0.02;
export const RAIL_GAP_RATIO = 0.9;
export const RAIL_VOICE_GAIN_PER = 0.12;
export const RAIL_VOICE_GAIN_MAX = 0.5;
export const RAIL_VOICE_FREQ_TAU = 0.04;
export const RAIL_VOICE_GAIN_TAU = 0.06;
export const RAIL_VOICE_RELEASE_S = 0.12;
export const RAIL_VOICE_LP_HZ = 2800;
export const RAIL_PAD_DETUNE_CENTS = 8;
export const RAIL_VIB_RATE_HZ = 5.5;
export const RAIL_VIB_DEPTH_RATIO = 0.015;
